// Core
import React, { useCallback } from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Plugins
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  dischargeRunningStatusToIndicatorStatus,
  isEquipmentRunning,
  overallStatusToTagType,
  overallStatusToText,
  permissiveStatusToIndicatorStatus,
} from '../../../../../../plugins/crusher';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Types
import { CrusherRunningSStatus } from '../../../../../../types/crusher';

// Components
import { Button, ButtonSize, ButtonType } from '../../../../../../layout/elements/Button';
import { Col } from '../../../../../../layout/elements/Col';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import { Speedometer } from '../../../../../../layout/elements/Speedometer';
import {
  StatusIndicator,
  StatusIndicatorSize,
} from '../../../../../../layout/elements/StatusIndicator';
import { Tag } from '../../../../../../layout/elements/Tag';
import { VibrationThermometer } from '../../../../../../components/VibrationThermometer';

// View
import { MotorStatusProps } from './MotorStatus.types';
import {
  BorderBottomContainer,
  CoastDownHistoryContainer,
  DriveSpeedContainer,
  Motor1AmpsContainer,
  Motor2AmpsContainer,
  MotorStatusContainer,
  OverallStatusContainer,
  PermissiveItem,
  PermissivesContainer,
  PermissivesTitleContainer,
  ShaftRpmContainer,
  VibrationContainer,
} from './MotorStatus.style';

function MotorStatus({
  unitData,
  edgeStatus,
  testId,
  className,
}: MotorStatusProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const { isXs } = useBreakpoint();

  /**
   * Handles the enabled/disabled motor 2 based on the enabled sensors.
   * @returns {number} - The number of columns to occupy.
   */
  const calculateMotor2ColSpan = useCallback((): number => {
    if (!unitData?.crusherMotorQty && !unitData?.crusherSpeedMonitorEnable) {
      return 0;
    }
    if (!unitData?.crusherMotorQty && unitData?.crusherSpeedMonitorEnable) {
      return 0;
    }
    if (unitData?.crusherMotorQty && !unitData?.crusherSpeedMonitorEnable) {
      return 12;
    }
    return 8;
  }, [unitData]);

  /**
   * Handles the enabled/disabled drive speed based on the enabled sensors.
   * @returns {number} - The number of columns to occupy.
   */
  const calculateDriveSpeedColSpan = useCallback((): number => {
    if (!unitData?.crusherMotorQty && !unitData?.crusherSpeedMonitorEnable) {
      return 24;
    }
    if (!unitData?.crusherMotorQty && unitData?.crusherSpeedMonitorEnable) {
      return 12;
    }
    if (unitData?.crusherMotorQty && !unitData?.crusherSpeedMonitorEnable) {
      return 12;
    }
    return 8;
  }, [unitData]);

  /**
   * Handles the enabled/disabled shaft RPM based on the enabled sensors.
   * @returns {number} - The number of columns to occupy.
   */
  const calculateShaftRpmColSpan = useCallback((): number => {
    if (!unitData?.crusherMotorQty && !unitData?.crusherSpeedMonitorEnable) {
      return 0;
    }
    if (!unitData?.crusherMotorQty && unitData?.crusherSpeedMonitorEnable) {
      return 12;
    }
    if (unitData?.crusherMotorQty && !unitData?.crusherSpeedMonitorEnable) {
      return 0;
    }
    return 8;
  }, [unitData]);

  const renderPermissive = (
    permissiveName: string,
    permissive: boolean | null | undefined,
  ): JSX.Element => (
    <PermissiveItem
      status={permissiveStatusToIndicatorStatus(
        permissive,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      )}
    >
      <StatusIndicator
        text={t(`view.crusher.vsi.drawers.motorStatus.${permissiveName}`)}
        status={permissiveStatusToIndicatorStatus(
          permissive,
          edgeStatus ? edgeStatus.edgeStatus : null,
          unitData?.timestamp,
        )}
        testId={`${testId}-permissive-indicator`}
      />
    </PermissiveItem>
  );

  const renderDischargePermissive = (
    permissiveName: string,
    permissive: CrusherRunningSStatus | null | undefined,
  ): JSX.Element => (
    <PermissiveItem
      status={dischargeRunningStatusToIndicatorStatus(
        permissive,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      )}
    >
      <StatusIndicator
        text={t(`view.crusher.vsi.drawers.motorStatus.${permissiveName}`)}
        status={dischargeRunningStatusToIndicatorStatus(
          permissive,
          edgeStatus ? edgeStatus.edgeStatus : null,
          unitData?.timestamp,
        )}
        testId={`${testId}-permissive-indicator`}
      />
    </PermissiveItem>
  );

  return (
    <MotorStatusContainer
      data-testid={testId}
      className={className}
    >
      <BorderBottomContainer>
        <Row>
          <Col>
            <OverallStatusContainer data-testid={`${testId}-overall-status-container`}>
              <Paragraph
                weight={ParagraphWeight.bold}
                size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                testId={`${testId}-overall-status-description`}
              >
                {t('view.crusher.overallStatus')}
              </Paragraph>
              <Tag
                text={overallStatusToText(unitData?.crusherMotorOverallStatus)}
                type={
                  overallStatusToTagType(
                    unitData?.crusherMotorOverallStatus,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
                testId="crusher-motor-overall-status-tag"
              />
            </OverallStatusContainer>
          </Col>
        </Row>
      </BorderBottomContainer>

      <BorderBottomContainer>
        <Row>
          {unitData?.vibrationSensor1Enable && (
            <Col cols={12}>
              <VibrationContainer data-testid={`${testId}-vibration-container`}>
                <VibrationThermometer
                  unit="%"
                  title={`${t('view.crusher.vsi.vibration.title')}`}
                  testId="vibration-sensor-1-thermometer"
                  minValue={0}
                  maxValue={100}
                  decimalPlaces={0}
                  value={unitData?.vibrationSensor1}
                  running={
                    isEquipmentRunning(
                      edgeStatus,
                      unitData?.timestamp,
                      DEVICE_COMMUNICATION_THRESHOLD,
                    )
                  }
                  thresholds={{
                    highWarning: unitData?.spVibration1Warning,
                  }}
                  className="vibration-thermometer"
                />
              </VibrationContainer>
            </Col>
          )}

          <Col cols={unitData?.vibrationSensor1Enable ? 12 : 24}>
            <Motor1AmpsContainer data-testid={`${testId}-motor-1-amps-speedometer-container`}>
              <Speedometer
                title={`${t('view.crusher.vsi.drawers.motorStatus.motor1Amps')}`}
                value={unitData?.crusherMotor1Amps}
                unit="A"
                minValue={0}
                maxValue={750}
                thresholds={{
                  highWarning: unitData?.spCrusherMotorFullLoadAmps,
                }}
                running={
                  isEquipmentRunning(
                    edgeStatus,
                    unitData?.timestamp,
                    DEVICE_COMMUNICATION_THRESHOLD,
                  )
                }
                testId="crusher-motor-1-amps-speedometer"
              />
            </Motor1AmpsContainer>
          </Col>
        </Row>
      </BorderBottomContainer>

      <BorderBottomContainer>
        <Row>
          {unitData?.crusherMotorQty && (
            <Col xs={24} xl={calculateMotor2ColSpan()}>
              <Motor2AmpsContainer data-testid={`${testId}-motor-2-amps-speedometer-container`}>
                <Speedometer
                  title={`${t('view.crusher.vsi.drawers.motorStatus.motor2Amps')}`}
                  value={unitData?.crusherMotor2Amps}
                  unit="A"
                  minValue={0}
                  maxValue={750}
                  thresholds={{
                    highWarning: unitData?.spCrusherMotorFullLoadAmps,
                  }}
                  running={
                    isEquipmentRunning(
                      edgeStatus,
                      unitData?.timestamp,
                      DEVICE_COMMUNICATION_THRESHOLD,
                    )
                  }
                  testId="crusher-motor-2-amps-speedometer"
                />
              </Motor2AmpsContainer>
            </Col>
          )}

          <Col xs={24} xl={calculateDriveSpeedColSpan()}>
            <DriveSpeedContainer
              data-testid={`${testId}-drive-speed-speedometer-container`}
              $hasRightBorder={!!unitData?.crusherSpeedMonitorEnable}
            >
              <Speedometer
                title={`${t('view.crusher.vsi.motorStatus.driveSpeed')}`}
                minValue={0}
                maxValue={10}
                value={unitData?.driveSpeedHz}
                unit="Hz"
                running={
                  isEquipmentRunning(
                    edgeStatus,
                    unitData?.timestamp,
                    DEVICE_COMMUNICATION_THRESHOLD,
                  )
                }
                thresholds={{}}
                testId="drive-speed-speedometer"
              />
            </DriveSpeedContainer>
          </Col>

          {unitData?.crusherSpeedMonitorEnable && (
            <Col xs={24} xl={calculateShaftRpmColSpan()}>
              <ShaftRpmContainer data-testid={`${testId}-shaft-rpm-speedometer-container`}>
                <Speedometer
                  title={`${t('view.crusher.vsi.motorStatus.shaftRpm')}`}
                  value={unitData?.crusherMotorRPM}
                  unit="rpm"
                  running={
                    isEquipmentRunning(
                      edgeStatus,
                      unitData?.timestamp,
                      DEVICE_COMMUNICATION_THRESHOLD,
                    )
                  }
                  minValue={0}
                  maxValue={3000}
                  thresholds={{
                    lowWarning: unitData?.spBeltSlipAlarmRpm,
                  }}
                  testId="crusher-motor-rpm-speedometer"
                />
              </ShaftRpmContainer>
            </Col>
          )}
        </Row>
      </BorderBottomContainer>

      <BorderBottomContainer>
        <Row>
          <Col>
            <PermissivesTitleContainer>
              <StatusIndicator
                text={t('view.crusher.vsi.drawers.motorStatus.crusherStartPermissives')}
                size={isXs ? StatusIndicatorSize.sm : StatusIndicatorSize.lg}
                status={permissiveStatusToIndicatorStatus(
                  unitData?.crusherStartPermissives,
                  edgeStatus ? edgeStatus.edgeStatus : null,
                  unitData?.timestamp,
                )}
                testId={`${testId}-crusher-start-permissive-indicator`}
              />
            </PermissivesTitleContainer>
          </Col>
        </Row>

        <Row>
          <Col xs={24} xl={12}>
            <PermissivesContainer
              $seamless
              data-testid={`${testId}-permissives-container-left`}
            >
              {renderPermissive('lubeOilLevelLow', unitData?.lubeOilLevelLow)}
              {renderPermissive('crusherBeltSlip', unitData?.crusherBeltSlip)}
              {renderPermissive('motor1Fault', unitData?.crusherMotor1Fault)}
              {unitData?.crusherMotorQty && (
                renderPermissive('motor2Fault', unitData?.crusherMotor2Fault)
              )}
              {renderPermissive('lubeReturnTempHigh', unitData?.lubeReturnTempHigh)}
              {renderPermissive('lowLubeOilGPM1', unitData?.lowLubeOilGPM1)}
              {renderPermissive('lowLubeOilGPM2', unitData?.lowLubeOilGPM2)}
              {renderPermissive('eStop', unitData?.eStop)}
              {renderPermissive('highVibrationPermissive', unitData?.highVibrationPermissive)}
            </PermissivesContainer>
          </Col>

          <Col xs={24} xl={12}>
            <PermissivesContainer data-testid={`${testId}-permissives-container-right`}>
              {renderDischargePermissive(
                'dischargeRunningStatus',
                unitData?.dischargeRunningStatus,
              )}
              {renderPermissive(
                'lubeReturnTempTransducerFault',
                unitData?.lubeReturnTempTransducerFault,
              )}
              {renderPermissive('lubeReturnLowTemp', unitData?.lubeReturnLowTemp)}
              {renderPermissive('minimumLubeRunTimer', unitData?.minimumLubeRunTimer)}
              {renderPermissive('lidClosed', unitData?.lidOpenStatus)}
              {renderPermissive('vibrationTransducerFault', unitData?.vibrationTransducerFault)}
              {renderPermissive('mandoorClosed', unitData?.mandoorClosed)}
              {renderPermissive('driveFault', unitData?.driveFault)}
            </PermissivesContainer>
          </Col>
        </Row>
      </BorderBottomContainer>

      <Row>
        <Col>
          <CoastDownHistoryContainer
            data-testid={`${testId}-coast-down-history-footer-container`}
          >
            <Paragraph
              weight={ParagraphWeight.bold}
              size={isXs ? ParagraphSize.sm : ParagraphSize.md}
              testId={`${testId}-coast-down-history-footer-description`}
            >
              {t('view.crusher.vsi.drawers.motorStatus.coastDownHistory')}
            </Paragraph>
            <Button
              type={ButtonType.secondary}
              disabled
              block={false}
              onClick={() => {}}
              size={isXs ? ButtonSize.xs : ButtonSize.md}
              testId={`${testId}-coast-down-history-footer-button`}
            >
              {t('common.openChart')}
            </Button>
          </CoastDownHistoryContainer>
        </Col>
      </Row>
    </MotorStatusContainer>
  );
}

export { MotorStatus };
